import { useTranslations } from '@lang';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuests } from '../../components/AddExistingGuests';
import { SectionWrapper } from '@common/components/BookingForms/Section';
import { Button } from '@common/components/Button';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar';
import { NotesWrapper, StyledDateRangeIndicator } from './MultiRoomGuestDetailsStep.styles';
import { RoomPartial } from './MultiRoomGuestDetailsStep.types';
import { Controller, useFormContext } from 'react-hook-form';
import { GuestFieldFormSchema } from '../../types';
import { ButtonWrapper } from '../../components/ExperienceDetails/ExperienceDetails.styles';
import { GuestDetails } from '../../components/GuestForm/GuestDetails';
import { TextArea } from '@common/components/FormElements/Textarea';
import { isGuestAlreadyInArray } from '@utils/helpers';
import { Title } from '@common/components/BookingForms';
import { some } from '@utils/some';
import { ExtrasSection } from '../ExtrasStep';
import { TitleWrapper } from '../GuestDetailsStep/GuestDetailsStep.styles';
import { useAddExistingGuest } from '../hooks/useAddExistingGuest';
import { MappedGuest } from '../../types';
import { mapLoggedInUserToGuest } from '../../helpers/mapLoggedInUserToGuest';
import { useState } from 'react';
import { usePackageConfiguration, useWizardNavigation, useCurrentStep } from '../../contexts';

export const MultiRoomGuestDetailsStep = () => {
  const { t } = useTranslations();

  const {
    journeyParams,
    rate,
    room,
    availableExtras = [],
    basketGuests,
    loggedInGuest,
  } = usePackageConfiguration();
  const { stickyBarId, handleNextStep } = useWizardNavigation();
  const { packageTotalPrice } = useCurrentStep();

  const { control, watch } = useFormContext();
  const guestsByRoom = watch('guests');
  const guests = (guestsByRoom?.rooms || []).flat();
  const isValid = GuestFieldFormSchema.safeParse(guestsByRoom).success;
  const mappedloggedInGuestData = mapLoggedInUserToGuest(loggedInGuest);
  const [availableGuests, setAvailableGuests] = useState<MappedGuest[]>(
    mappedloggedInGuestData ? [mappedloggedInGuestData] : []
  );
  const { handleGuestPillClick, selectedGuests, existingGuests } = useAddExistingGuest({
    availableGuests,
    basketGuests,
  });
  const updateAvailableGuestsOnSubmit = () => {
    const updatedAvailableGuests = availableGuests;
    guests.forEach((guest) => {
      if (
        guest.firstName &&
        guest.lastName &&
        !isGuestAlreadyInArray(updatedAvailableGuests, guest)
      ) {
        updatedAvailableGuests.push(guest);
      }
    });
    setAvailableGuests(updatedAvailableGuests);
  };
  const onSubmit = () => {
    updateAvailableGuestsOnSubmit();
    handleNextStep();
  };

  const selectedEntity = room;
  const metaEntity = rate;

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        <AddExistingGuests
          selectedGuests={selectedGuests}
          existingGuests={existingGuests as Guest[]}
          isGenderSpecific={false}
          guest={loggedInGuest as MappedGuest}
          handleGuestPillClick={handleGuestPillClick}
        />
        {(journeyParams?.rooms || []).map((r: RoomPartial) => {
          return (
            <>
              {`${t('common.room')} ${r.roomNumber}`}
              <GuestDetails
                isGenderSpecific={false}
                numberOfGuests={r.adults}
                isDisabled={false}
                withGenderSelect={false}
                roomNumber={r.roomNumber - 1}
              />
              {r.children ? <>+{t('basket.children', { children: r.children })}</> : null}
            </>
          );
        })}
        <NotesWrapper>
          <Controller
            name={'guests.notes'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextArea
                isFullWidth
                labelWidth="150px"
                name="note"
                label={t('rooms.notes')}
                id="additional-notes"
                onChange={(val) => {
                  if (val) {
                    onChange(val);
                  }
                }}
                input={{
                  value,
                }}
                error={''}
              />
            )}
          />
        </NotesWrapper>
      </SectionWrapper>
      {some(availableExtras) && (
        <>
          <TitleWrapper>
            <Title titleId={'room.extrasStepTitle'} />
          </TitleWrapper>
          <ExtrasSection />
        </>
      )}
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={journeyParams.fromDate}
              toDate={journeyParams.toDate}
            />
          }
          displayedPrice={packageTotalPrice}
          button={
            <ButtonWrapper>
              <Button isPrimary onClick={onSubmit} disabled={!isValid}>
                {t('spa.package.nextStep')}
              </Button>
            </ButtonWrapper>
          }
        />
      </Portal>
    </form>
  ) : null;
};
